import type { ReactElement, ReactNode } from "react";
import { ResponsiveContainer } from "recharts";
import { formatNumber } from "../lib/digits";
import { type JalaliStyle, formatJalali } from "../lib/jalali";
import { formatTomanCompact } from "../lib/money";
import { cx } from "../utils/cx";

export interface ChartFrameProps {
    title?: ReactNode;
    description?: ReactNode;
    /** Buttons or tabs at the inline-end of the header (e.g. 30/90-day switch). */
    actions?: ReactNode;
    /** Legend row under the header (RTL). */
    legend?: ReactNode;
    /** Chart height in px. */
    height?: number;
    /** A single Recharts chart element (AreaChart, BarChart...). */
    children: ReactElement;
    footer?: ReactNode;
    /** Render without the card chrome (inside another card). */
    bare?: boolean;
    /** Fill the parent's height (grid row with taller neighbours): the plot grows, `height` is its minimum. */
    stretch?: boolean;
    /** Message shown instead of the chart when there is nothing to plot. */
    empty?: ReactNode;
    className?: string;
}

/**
 * Card + LTR island for Recharts (research Q4): Recharts ignores CSS direction, so the SVG lives in
 * `dir="ltr"` and time runs left to right; titles, legend and tooltips stay RTL. Ticks use Jalali dates
 * and Persian digits through the helpers below.
 */
export const ChartFrame = ({ title, description, actions, legend, height = 240, children, footer, bare = false, stretch = false, empty, className }: ChartFrameProps) => (
    <section className={cx(!bare && "rounded-2xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", stretch && "flex h-full flex-col", className)}>
        {(title || actions) && (
            <header className={cx("flex flex-wrap items-start justify-between gap-3", !bare && "px-4 pt-5 md:px-6")}>
                <div className="flex min-w-0 flex-col gap-0.5">
                    {title && <h3 className="text-lg font-semibold text-primary">{title}</h3>}
                    {description && <p className="text-sm text-tertiary">{description}</p>}
                </div>
                {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
            </header>
        )}
        {legend && <div className={cx("pt-3", !bare && "px-4 md:px-6")}>{legend}</div>}
        <div dir="ltr" className={cx("w-full", stretch && "flex-1", !bare && "px-2 pt-4 pb-3 md:px-4")} style={stretch ? { minHeight: height } : { height }}>
            {empty ? (
                <div dir="rtl" className="flex h-full w-full items-center justify-center px-6 text-center text-sm leading-7 text-tertiary">
                    <p className="max-w-md">{empty}</p>
                </div>
            ) : (
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
                    {children}
                </ResponsiveContainer>
            )}
        </div>
        {footer && <footer className={cx("border-t border-secondary", !bare && "px-4 py-3 md:px-6")}>{footer}</footer>}
    </section>
);

/** X-axis tick formatter for ISO dates: Jalali «12 مهر» by default. */
export const jalaliTick =
    (style: JalaliStyle = "dayMonth") =>
    (value: string | number | Date) =>
        formatJalali(value, style);

/** Y-axis tick formatter for toman amounts: «12٫5 میلیون». */
export const tomanTick = (value: number) => formatTomanCompact(value);

/** Plain count tick with Persian grouping. */
export const countTick = (value: number) => formatNumber(value);
